<template>
  <div>
    <div :class="[containerClass, 'gradebook-wide-container']">
      <page-header
        :title="'Gradebook'"
        :container-class="null"
        :info-text="'View student grades for assignments and assessments'"
        :show-back="true"
        class="mb-32pt"
      />
    </div>

    <div class="page-section">
      <div :class="[containerClass, 'gradebook-wide-container']">
        <div class="gradebook-section">
          <div v-if="!isLoading && cohortExists === false" class="cohort-helper-text">
            Grading criteria not set. Default assignment and assessment weightages will apply
          </div>
          <div class="gradebook-card mb-0">
            <div class="gradebook-card__header">
              <div class="header-filters">
                <div class="filters-left">
                  <div class="search-bar-wrapper">
                    <input
                      type="text"
                      v-model="searchQuery"
                      @input="onSearch"
                      placeholder="Search by student name..."
                      class="search-input"
                    />
                    <i class="material-icons search-icon">search</i>
                  </div>

                  <div class="tag-filter-wrapper">
                    <select
                      v-model="selectedTagId"
                      @change="onTagChange"
                      class="tag-select"
                      :disabled="availableTags.length === 0"
                    >
                      <option :value="null" v-if="availableTags.length === 0">
                        No Tags Available
                      </option>
                      <option
                        v-for="tag in availableTags"
                        :key="tag.id"
                        :value="tag.id"
                      >
                        {{ tag.title }}
                      </option>
                    </select>
                  </div>
                </div>

                <div class="filters-right">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    class="export-excel-btn"
                    :disabled="exportingExcel || students.length === 0"
                    @click="exportGradebookToExcel"
                  >
                    <b-spinner v-if="exportingExcel" small class="mr-1" />
                    <i v-else class="fas fa-file-excel mr-1"></i>
                    Export to Excel
                  </b-button>
                  <button
                    @click="navigateToCohortConfig"
                    :disabled="!selectedTagId"
                    class="btn-configure-cohort"
                    :class="{ 'btn-disabled': !selectedTagId }"
                  >
                    <i class="material-icons">settings</i>
                    Configure Grading Criteria
                  </button>
                </div>
              </div>
            </div>
            <div class="gradebook-card__body">
              <div v-if="isLoading" class="text-center my-5">
                <b-spinner class="align-middle mr-2"></b-spinner>
                <strong>Loading gradebook...</strong>
              </div>

              <div v-else-if="students.length" class="table-responsive gradebook-table-wrapper">
                <table class="gradebook-table">
                  <thead>
                    <tr>
                      <th class="sticky-col student-col"></th>
                      <th
                        v-for="column in columns"
                        :key="column.id"
                        :class="['assignment-header', column.type === 'metric' ? 'metric-header' : '']"
                      >
                        <div class="assignment-header-content">
                          <!-- Metric columns - simple and clean -->
                          <template v-if="column.type === 'metric'">
                            <div class="metric-badge-small">Summary</div>
                            <div class="metric-title">{{ column.title }}</div>
                            <div v-if="column.description" class="metric-description">{{ column.description }}</div>
                          </template>
                          
                          <!-- Quiz/Assignment columns -->
                          <template v-else>
                            <div class="assignment-type-badge" :class="column.type === 'quiz' ? 'badge-quiz' : 'badge-assignment'">
                              {{ column.type === 'quiz' ? 'Assessment' : 'Assignment' }}
                            </div>
                            <div class="assignment-title">{{ column.title }}</div>
                            <div class="assignment-weightage">
                              Weightage: {{ column.weightage }}%
                            </div>
                            <div v-if="column.chapter_title || column.lesson_title" class="assignment-context">
                              <span v-if="column.chapter_title">{{ column.chapter_title }}</span>
                              <span v-if="column.lesson_title"> • {{ column.lesson_title }}</span>
                            </div>
                            
                          </template>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <!-- Student Rows -->
                    <tr v-for="student in sortedStudents" :key="student.student_id" @click="navigateToStudentProgress(student)" class="clickable-row">
                      <td class="sticky-col student-col">
                        <div class="student-info">
                          <div class="student-avatar-wrapper">
                            <img 
                              v-if="student.student_avatar" 
                              :src="student.student_avatar" 
                              :alt="student.student_name"
                              class="student-avatar"
                            />
                            <div 
                              v-else 
                              class="student-avatar student-avatar-fallback"
                            >
                              {{ student.initials }}
                            </div>
                          </div>
                          <div class="student-details">
                            <div class="student-name">{{ student.student_name }}</div>
                            <div class="student-email">{{ student.student_email }}</div>
                          </div>
                        </div>
                      </td>
                      <td
                        v-for="column in columns"
                        :key="`${student.student_id}-${column.id}-${column.chapter_id || 'no-chapter'}-${column.lesson_id || 'no-lesson'}`"
                        class="score-cell"
                      >
                        <!-- Overall Progress with progress bar -->
                        <div v-if="column.id === 'overall_progress' && getStudentGrade(student, column)" class="progress-bar-container">
                          <div class="progress-bar-wrapper">
                            <div 
                              class="progress-bar-fill" 
                              :style="{ width: formatScore(getStudentGrade(student, column)) }"
                              :class="getProgressBarClass(getStudentGrade(student, column))"
                            ></div>
                          </div>
                          <span class="progress-percentage">{{ formatScore(getStudentGrade(student, column)) }}</span>
                        </div>
                        
                        <!-- Overall Grade with badge -->
                        <div v-else-if="column.id === 'overall_grade' && formatScore(getStudentGrade(student, column))"
                             :class="['grade-badge', getScoreClass(getStudentGrade(student, column))]">
                          {{ formatScore(getStudentGrade(student, column)) }}
                        </div>

                        <!-- Overall Grade before any graded work: not a zero -->
                        <span v-else-if="column.id === 'overall_grade'" class="score-status">
                          No graded work
                        </span>
                        
                        <!-- Regular score display for other columns (assessments/assignments) -->
                        <span
                          v-else-if="shouldShowMarks(student, column)"
                          :class="['score-value', getScoreClass(getStudentGrade(student, column))]"
                        >
                          {{ formatScoreMarks(getStudentGrade(student, column)) }}
                        </span>
                        
                        <!-- Status display -->
                        <span v-else class="score-status">
                          {{ getScoreStatus(student, column) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="text-center py-5">
                <p class="text-muted mb-0">No gradebook data found</p>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="students.length" class="gradebook-card__footer">
              <pagination
                v-model="currentPage"
                :total-rows="totalStudents"
                :per-page="perPage"
                @change="onPageChange"
                aria-controls="gradebook-table"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce, get } from 'lodash';
import { mapActions } from 'vuex';
import axios from 'axios';
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import { DEFAULT_PAGE_SIZE } from '../../../common/constants';

export default {
  components: {
    PageHeader,
    Pagination,
  },
  extends: Page,

  data() {
    return {
      title: 'Gradebook',
      courseId: null,
      courseName: '',
      programCategoryId: null,
      availableTags: [],
      selectedTagId: null,
      cohortExists: null,
      isLoading: true,
      sortBy: 'lastName',
      sortOptions: [
        { label: 'Last Name', value: 'lastName' },
        { label: 'First Name', value: 'firstName' },
      ],
      searchQuery: '',
      currentPage: 1,
      perPage: DEFAULT_PAGE_SIZE,
      totalStudents: 0,
      columns: [],
      students: [],
      exportingExcel: false,
    };
  },

  computed: {
    sortedStudents() {
      const students = [...this.students];
      if (this.sortBy === 'lastName') {
        return students.sort((a, b) => {
          const aLast = this.getLastName(a.student_name);
          const bLast = this.getLastName(b.student_name);
          return aLast.localeCompare(bLast);
        });
      } else if (this.sortBy === 'firstName') {
        return students.sort((a, b) => {
          const aFirst = this.getFirstName(a.student_name);
          const bFirst = this.getFirstName(b.student_name);
          return aFirst.localeCompare(bFirst);
        });
      }
      return students;
    },
  },

  methods: {
    ...mapActions('courses', ['getCourse', 'getCourseGradebook']),
    ...mapActions('attendance', ['getTags']),

    onSearch() {
      this.currentPage = 1;
      this.debouncedSearch();
    },

    debouncedSearch: debounce(function() {
      this.fetchGradebookData();
    }, 500),

    onPageChange(pageNum) {
      this.currentPage = pageNum;
      this.fetchGradebookData();
    },

    onTagChange() {
      this.currentPage = 1;
      this.fetchGradebookData();
    },

    sortStudents() {
      // Sorting is handled by the computed property
    },

    navigateToCohortConfig() {
      if (this.selectedTagId) {
        this.$router.push({
          name: 'cohort-config',
          params: {
            courseId: this.courseId,
            tagId: this.selectedTagId,
          },
        });
      }
    },

    navigateToStudentProgress(student) {
      const enrollmentId = student.course_log_id;
      
      if (!enrollmentId) {
        this.makeToast({ 
          variant: 'warning', 
          msg: 'Enrollment information not available for this student' 
        });
        return;
      }

      this.$router.push({
        name: 'course-enrollment-gradebook',
        params: { enrollmentId: enrollmentId }
      });
    },

    getFirstName(fullName) {
      if (!fullName) return '';
      return fullName.split(' ')[0] || '';
    },

    getLastName(fullName) {
      if (!fullName) return '';
      const parts = fullName.split(' ');
      return parts.length > 1 ? parts[parts.length - 1] : fullName;
    },

    getInitials(fullName) {
      if (!fullName) return '?';
      const parts = fullName.trim().split(' ');
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase();
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    },

    getStudentGrade(student, column) {
      // Match by assessment_id, lesson_id, and chapter_id to get the correct context
      const grade = student.grades?.find(g => 
        g.assessment_id === column.id &&
        g.lesson_id === column.lesson_id &&
        g.chapter_id === column.chapter_id
      );
      return grade && grade.percentage !== null ? grade : null;
    },

    shouldShowMarks(student, column) {
      // Get the grade data for the student
      const grade = student.grades?.find(g => 
        g.assessment_id === column.id &&
        g.lesson_id === column.lesson_id &&
        g.chapter_id === column.chapter_id
      );

      if (!grade) return false;

      // For quizzes: show marks if status is graded
      if (grade.assessment_type === 'quiz') {
        return grade.status === 'graded';
      }

      // For assignments:
      if (grade.assessment_type === 'assignment') {
        // If graded is false, never show marks (only show status)
        if (grade.graded === false) {
          return false;
        }
        // If graded is true, show marks only when status is graded
        if (grade.graded === true) {
          return grade.status === 'graded';
        }
      }

      return false;
    },

    getScoreStatus(student, column) {
      // Match by assessment_id, lesson_id, and chapter_id to get the correct context
      const grade = student.grades?.find(g => 
        g.assessment_id === column.id &&
        g.lesson_id === column.lesson_id &&
        g.chapter_id === column.chapter_id
      );
      
      if (!grade || grade.status === 'not_assigned') {
        return 'Pending';
      }

      if (grade.status === 'not_graded') {
        return 'Not graded';
      }
      if (grade.status === 'submitted') {
        return 'Submitted';
      }
      if (grade.status === 'pending' || grade.status === 'draft') {
        return 'Draft - Missing';
      }
      return 'Pending';
    },

    formatScore(grade) {
      if (!grade || grade.percentage === null) return '';
      const percentage = parseFloat(grade.percentage);
      return `${percentage.toFixed(0)}%`;
    },

    formatScoreMarks(grade) {
      if (!grade) return '';
      const scoreObtained = parseFloat(grade.score_obtained || 0);
      const totalMarks = grade.total_marks || 0;
      return `${scoreObtained.toFixed(0)}/${totalMarks}`;
    },

    getScoreClass(grade) {
      if (!grade || grade.percentage === null) return '';
      const percentage = parseFloat(grade.percentage);
      
      if (percentage >= 90) return 'score-excellent';
      if (percentage >= 80) return 'score-good';
      if (percentage >= 70) return 'score-average';
      if (percentage >= 60) return 'score-below-average';
      return 'score-poor';
    },

    getProgressBarClass(grade) {
      if (!grade || grade.percentage === null) return '';
      const percentage = parseFloat(grade.percentage);
      
      if (percentage >= 90) return 'progress-excellent';
      if (percentage >= 80) return 'progress-good';
      if (percentage >= 70) return 'progress-average';
      if (percentage >= 60) return 'progress-below-average';
      return 'progress-poor';
    },

    async fetchCourseDetails() {
      try {
        const response = await this.getCourse(this.courseId);
        
        // The actual course data is nested inside response.data
        const course = response?.data;
        
        this.courseName = course?.title || 'Course';
        this.programCategoryId = course?.program_category?.id || null;
        
        // Fetch tags if program category exists
        if (this.programCategoryId) {
          await this.fetchTags();
          // After tags are fetched and latest tag is selected, fetch gradebook data
          await this.fetchGradebookData();
        }
      } catch (e) {
        console.error('Error fetching course details:', e);
        this.courseName = 'Course';
        this.programCategoryId = null;
      }
    },

    async fetchTags() {
      try {
        const params = { program__id: this.programCategoryId };
        
        const response = await this.getTags(params);
        
        if (response?.data?.data) {
          this.availableTags = response.data.data;
          // Preselect the first tag if available and no tag is currently selected
          if (this.availableTags.length > 0 && !this.selectedTagId) {
            this.selectedTagId = this.availableTags[0].id;
          }
        } else {
          this.availableTags = [];
        }
      } catch (e) {
        this.availableTags = [];
      }
    },

    async fetchGradebookData() {
      this.isLoading = true;
      try {
        const params = {
          course_id: this.courseId,
          limit: this.perPage,
          offset: (this.currentPage - 1) * this.perPage,
          ...(this.searchQuery && { search: this.searchQuery }),
          ...(this.selectedTagId && { student__selected_tag: this.selectedTagId }),
        };

        const response = await this.getCourseGradebook(params);

        // Support both possible shapes: response.data.cohort_exists OR response.data.results.cohort_exists
        this.cohortExists = response?.data?.cohort_exists ?? response?.data?.results?.cohort_exists ?? null;
        
        if (response.data && response.data.results) {
          const results = response.data.results;
          this.columns = results.columns || [];
          this.totalStudents = response.data.count || 0;
          
          // Process students and add avatar data
          this.students = (results.students || []).map((student) => ({
            ...student,
            initials: this.getInitials(student.student_name),
          }));
        } else {
          this.columns = [];
          this.students = [];
          this.totalStudents = 0;
        }
      } catch (e) {
        console.error('Failed to load gradebook:', e);
        this.makeToast({ variant: 'danger', msg: 'Failed to load gradebook data' });
        this.columns = [];
        this.students = [];
        this.totalStudents = 0;
        this.cohortExists = null;
      }
      this.isLoading = false;
    },

    async exportGradebookToExcel() {
      if (this.exportingExcel || this.students.length === 0) return;

      this.exportingExcel = true;
      try {
        const courseName = this.courseName || '-';
        
        // Build columns array for export
        const columns = this.columns.map((column) => {
          if (column.type === 'metric') {
            return {
              title: column.title,
              type: 'summary',
              subtitle: '',
            };
          } else {
            const subtitleParts = [];
            if (column.weightage !== null && column.weightage !== undefined) {
              subtitleParts.push(`Weightage: ${Number(column.weightage).toFixed(2)}%`);
            }
            const contextParts = [];
            if (column.chapter_title) {
              contextParts.push(column.chapter_title);
            }
            if (column.lesson_title) {
              contextParts.push(column.lesson_title);
            }
            if (contextParts.length > 0) {
              subtitleParts.push(contextParts.join(' + '));
            }
            
            return {
              title: column.title,
              type: column.type === 'quiz' ? 'assessment' : 'assignment',
              subtitle: subtitleParts.join('\n'),
            };
          }
        });

        // Build students array for export
        const students = this.students.map((student) => {
          const grades = this.columns.map((column) => {
            const grade = student.grades?.find(g => 
              g.assessment_id === column.id &&
              g.lesson_id === column.lesson_id &&
              g.chapter_id === column.chapter_id
            );

            // For metric columns (Overall Progress, Overall Grade)
            if (column.type === 'metric') {
              return this.formatScore(grade) || '-';
            }

            // For quiz/assignment columns
            if (this.shouldShowMarks(student, column)) {
              return this.formatScoreMarks(grade);
            } else {
              return this.getScoreStatus(student, column);
            }
          });

          return {
            name: student.student_name || '-',
            email: student.student_email || '-',
            grades,
          };
        });

        const response = await axios.post(
          'api/lms/course-log/export-gradebook/',
          { course_name: courseName, columns, students }
        );

        const fileUrl = get(response, 'data.data.file_url') || get(response, 'data.file_url');
        if (!fileUrl) {
          this.makeToast({ variant: 'danger', msg: 'No file URL returned from server.' });
          return;
        }

        const link = document.createElement('a');
        link.href = fileUrl;
        link.setAttribute('download', `${courseName}_gradebook.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        this.makeToast({ variant: 'success', msg: 'Gradebook exported successfully!' });
      } catch (error) {
        console.error('Export error:', error);
        this.makeToast({ variant: 'danger', msg: 'Failed to export gradebook.' });
      } finally {
        this.exportingExcel = false;
      }
    },
  },

  async mounted() {
    this.courseId = this.$route.params.id;
    if (!this.courseId) {
      this.makeToast({ variant: 'danger', msg: 'Course ID is required' });
      this.$router.go(-1);
      return;
    }
    // Fetch course details first, which will fetch tags and then gradebook data
    await this.fetchCourseDetails();
  },
};
</script>

<style scoped lang="scss">
// ADJUST THIS VALUE to control horizontal scrolling:
// - Smaller value (e.g., 900px) = more scrolling
// - Larger value (e.g., 1600px) = less scrolling
.gradebook-wide-container {
  max-width: 1400px;
}

.gradebook-section {
  margin-top: 1rem;
}

.gradebook-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  background: #fff;
}

.gradebook-card__header {
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.header-filters {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.filters-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.filters-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-bar-wrapper {
  position: relative;
  flex: 0 0 240px;
  max-width: 240px;
  width: 100%;
  min-width: 180px;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.2s ease;
  background: #f9fafb;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: #fff;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.08);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 18px;
  pointer-events: none;
}

.tag-filter-wrapper {
  flex: 1 1 auto;
  max-width: 350px;
  width: 100%;
  min-width: 250px;
}

.tag-select {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.2s ease;
  background: #f9fafb;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #667eea;
    background: #fff;
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f3f4f6;
  }

  option {
    color: #1f2937;
    background: #fff;
  }
}

.cohort-helper-text {
  width: 100%;
  padding: 10px 16px;
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 600;
  // The app's "heads up, you may want to act" amber, same as the notices on
  // CohortConfig -- the page Configure Grading Criteria links to. Deliberately
  // not a brand color: this is a status message, and a school's secondary can
  // be red or green, which would read as an error or an all-clear.
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
}

.info-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: help;

  .info-icon {
    font-size: 20px;
    color: #667eea;
    transition: color 0.2s ease;
  }

  &:hover .info-icon {
    color: #5568d3;
  }

  .tooltip-content {
    position: absolute;
    bottom: calc(100% + 8px);
    right: 0;
    background: #1f2937;
    color: #fff;
    padding: 10px 14px;
    border-radius: 6px;
    font-size: 0.8125rem;
    line-height: 1.4;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    max-width: 280px;
    white-space: normal;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      right: 12px;
      border: 6px solid transparent;
      border-top-color: #1f2937;
    }
  }

  &:hover .tooltip-content {
    opacity: 1;
    visibility: visible;
  }
}

.export-excel-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.btn-configure-cohort {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color, #667eea);
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  i.material-icons {
    font-size: 18px;
  }

  &:hover:not(:disabled) {
    background: var(--primary-color-dark, #5568d3);
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled,
  &.btn-disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: #9ca3af;
  }
}


.sort-dropdown-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
}

.sort-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4b5563;
  margin-bottom: 0;
}

.sort-select {
  min-width: 140px;
}

.gradebook-card__body {
  padding: 0;
}

.gradebook-card__footer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
}

.gradebook-table-wrapper {
  overflow-x: auto;
  max-width: 100%;
}

// ADJUST THIS VALUE to force horizontal scrolling:
// - Increase this number to force more scrolling (e.g., 1200px, 1500px)
// - Decrease to allow less scrolling (e.g., 600px, 500px)
.gradebook-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 900px;  // Reduced from 800px to enable scrolling
}

.gradebook-table thead {
  background: #f9fafb;
}

.gradebook-table thead th {
  padding: 10px 8px;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  border-bottom: 1px solid #e5e7eb;
  vertical-align: top;
  white-space: normal;
  min-width: 120px;
}

.gradebook-table thead th.student-col {
  text-align: left;
  min-width: 220px;
  max-width: 220px;
  width: 220px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: #fff;
  z-index: 10;
  box-shadow: 2px 0 3px rgba(0, 0, 0, 0.04);
}

.gradebook-table thead .sticky-col {
  background: #f9fafb;
}

.assignment-header {
  position: relative;
  
  &.metric-header {
    background: #fafafa;
    border-left: 1px solid #e0e0e0;
    border-right: 1px solid #e0e0e0;
  }
}

.assignment-header-content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
}

.metric-badge-small {
  font-size: 0.575rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 8px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #f3f4f6;
  color: #6b7280;
  border: 1px solid #e5e7eb;
}

.metric-title {
  font-size: 0.75rem;
  color: #374151;
  font-weight: 700;
  text-align: center;
  line-height: 1.3;
  padding: 2px 0;
  margin-top: 15px;
}

.metric-description {
  font-size: 0.625rem;
  color: #6b7280;
  text-align: center;
  line-height: 1.3;
}

.assignment-type-badge {
  font-size: 0.6rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  
  // Assessment badge - Purple/Indigo theme
  &.badge-quiz {
    background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
    color: #4338ca;
    border: 1px solid #c7d2fe;
  }
  
  // Graded Assignment badge - Teal/Emerald theme
  &.badge-assignment {
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
    color: #047857;
    border: 1px solid #a7f3d0;
  }
}

.assignment-title {
  font-size: 0.75rem;
  color: #1f2937;
  font-weight: 600;
  line-height: 1.2;
  max-width: 120px;
  word-wrap: break-word;
  text-align: center;
  margin-top: 2px;
}

.assignment-context {
  font-size: 0.625rem;
  color: #6b7280;
  font-weight: 500;
  text-align: center;
  max-width: 120px;
  word-wrap: break-word;
  line-height: 1.1;
}

.assignment-points {
  font-size: 0.65rem;
  color: #9ca3af;
  font-weight: 500;
}

.assignment-weightage {
  font-size: 0.625rem;
  color: #4b5563;
  font-weight: 700;
}

.gradebook-table tbody tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s ease;
  cursor: pointer;
}

.gradebook-table tbody tr:hover {
  background-color: #f9fafb;
}

.gradebook-table tbody tr:hover .sticky-col {
  background-color: #f9fafb;
}

.gradebook-table tbody td {
  padding: 10px 8px;
  font-size: 0.8125rem;
  color: #1f2937;
  vertical-align: middle;
  text-align: center;
}

.gradebook-table tbody td.student-col {
  text-align: left;
}

.student-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.student-avatar-wrapper {
  flex-shrink: 0;
}

.student-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e5e7eb;
}

.student-avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  font-size: 0.75rem;
  border: none;
  background-color: var(--primary-color);
}

.student-details {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.student-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.8125rem;
  line-height: 1.3;
}

.student-email {
  font-size: 0.7rem;
  color: #6b7280;
  line-height: 1.2;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.score-cell {
  text-align: center;
}

.score-value {
  font-weight: 600;
  color: #1f2937;
  font-size: 0.8125rem;
}

.score-status {
  font-size: 0.75rem;
  color: #9ca3af;
  font-style: italic;
}

.progress-bar-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 0 4px;
}

.progress-bar-wrapper {
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
  margin-top: 20px;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
  background: var(--primary-color);
  // margin-top: 10px;
}

.progress-percentage {
  font-size: 0.7rem;
  font-weight: 600;
  color: #4b5563;
}

.grade-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 1000;
  color: var(--primary-color);
}

@media (max-width: 991.98px) {
  .header-filters {
    flex-direction: column;
    align-items: stretch;
  }

  .search-bar-wrapper {
    flex: 1;
    max-width: 100%;
  }

  .tag-filter-wrapper {
    flex: 1;
    max-width: 100%;
  }



  .sort-dropdown-wrapper {
    width: 100%;
    margin-left: 0;
  }
}

@media (max-width: 767.98px) {
  .gradebook-table {
    font-size: 0.85rem;

    thead tr th,
    tbody tr td {
      padding: 12px 8px;
    }
  }

  .sort-dropdown-wrapper {
    flex-direction: column;
    align-items: flex-start;
  }

  .sort-select {
    width: 100%;
  }
}
</style>
